import { Button, ButtonProps, LinkProps, TypographyOwnProps } from "@mui/material";
import {
    functionalButtonColors,
    SenecButtonColorTheme,
    SenecButtonLevel,
    SenecButtonSize,
} from "src/components/button/ButtonUtils";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";
import { styled } from "src/styles/fpp-theme/fppStyled";

export const functionalButtonHeightSmall = "32px";
export const functionalButtonHeightMedium = "40px";

export interface SenecFunctionalButtonProps
    extends Omit<ButtonProps, "color" | "variant" | "size">,
        Omit<LinkProps, keyof TypographyOwnProps | keyof ButtonProps> {
    level?: SenecButtonLevel;
    size?: SenecButtonSize;
    colorTheme?: SenecButtonColorTheme;
}

export const SenecFunctionalButton = ({
    level = "primary",
    size = "medium",
    colorTheme = "light",
    children,
    ...rest
}: SenecFunctionalButtonProps) => {
    return (
        <StyledFunctionalButton level={level} size={size} colorTheme={colorTheme} {...rest}>
            {children}
        </StyledFunctionalButton>
    );
};
interface StyledFunctionalButtonProps extends ButtonProps, Omit<LinkProps, keyof TypographyOwnProps | keyof ButtonProps> {
    level: SenecButtonLevel;
    size: SenecButtonSize;
    colorTheme: SenecButtonColorTheme;
}

const StyledFunctionalButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== "colorTheme",
})<StyledFunctionalButtonProps>(
    ({
        theme,
        level,
        size,
        colorTheme,
    }: {
        theme: SenecFPPTheme;
        level: SenecButtonLevel;
        size: SenecButtonSize;
        colorTheme: SenecButtonColorTheme;
    }) => ({
        ...functionalButtonStyles(theme, level, size, colorTheme),
    }),
);

export const functionalButtonStyles = (
    theme: SenecFPPTheme,
    level: SenecButtonLevel,
    size: SenecButtonSize,
    colorTheme: SenecButtonColorTheme,
) => {
    const palette = functionalButtonColors(theme);

    return {
        // Currently disabled EnBW Font since it cause a bug with icon alignment:
        // https://github.com/mui/material-ui/issues/40277
        // Please remove the fontFamily override underneath
        borderRadius: "0.5rem",
        justifyContent: "center",
        alignItems: "center",
        whiteSpace: "nowrap",
        letterSpacing: 0,
        "&.Mui-focusVisible": {
            outline: `${theme.palette.azur60.main} solid 2px`,
            outlineOffset: "2px",
        },
        "& .MuiButton-startIcon > .MuiSvgIcon-root": {
            fontSize: "24px",
        },
        "& .MuiButton-endIcon > .MuiSvgIcon-root": {
            fontSize: "24px",
        },
        ...(level === "primary" && {
            color: palette[colorTheme].primaryText,
            backgroundColor: palette[colorTheme].primaryMain,
            "&:hover": {
                color: theme.palette.white.main,
                backgroundColor: palette[colorTheme].primaryLight,
            },
            "&:active": {
                color: theme.palette.white.main,
                backgroundColor: palette[colorTheme].primaryDark,
            },
            "&.Mui-disabled": {
                color: palette[colorTheme].primaryText,
                backgroundColor: palette[colorTheme].primaryMain,
                opacity: "0.7",
            },
        }),
        ...(level === "secondary" && {
            color: palette[colorTheme].secondaryText,
            backgroundColor: palette[colorTheme].secondaryMain,
            "&:hover": {
                backgroundColor: palette[colorTheme].secondaryLight,
            },
            "&:active": {
                color: theme.palette.white.main,
                backgroundColor: palette[colorTheme].secondaryDark,
            },
            "&.Mui-focusVisible": {
                border: `1px solid ${theme.palette.neutralGray40.main}`,
                outline: `${theme.palette.azur60.main} solid 2px`,
                outlineOffset: "2px",
            },
            "&.Mui-disabled": {
                color: palette[colorTheme].secondaryText,
                backgroundColor: palette[colorTheme].secondaryMain,
                opacity: "0.7",
            },
        }),
        ...(level === "tertiary" && {
            color: palette[colorTheme].tertiaryText,
            backgroundColor: "transparent",
            "&:hover": {
                backgroundColor: palette[colorTheme].tertiaryLight,
            },
            "&:active": {
                color: theme.palette.white.main,
                backgroundColor: palette[colorTheme].tertiaryDark,
            },
            "&.Mui-disabled": {
                color: palette[colorTheme].tertiaryText,
                opacity: "0.7",
            },
        }),
        ...(size === "small" && {
            ...theme.typography.enbwTabletS2Small,
            fontFamily: "Mark Pro, sans-serif",
            height: functionalButtonHeightSmall,
            padding: "0.125rem 0.625rem",
            "& .MuiButton-startIcon": {
                marginRight: "0.125rem",
                marginLeft: "-0.5rem",
            },
            "& .MuiButton-endIcon": {
                marginLeft: "0.125rem",
                marginRight: "-0.5rem",
            },
        }),
        ...(size === "medium" && {
            ...theme.typography.enbwTabletS1,
            fontFamily: "Mark Pro, sans-serif",
            height: functionalButtonHeightMedium,
            padding: "0.25rem 0.75rem",
            "& .MuiButton-startIcon": {
                marginRight: "0.25rem",
                marginLeft: "-0.25rem",
            },
            "& .MuiButton-endIcon": {
                marginLeft: "0.25rem",
                marginRight: "-0.25rem",
            },
        }),
    };
};
